import { ReitElementApi, ReitElementRating } from 'types/reit-element';
import big from 'big.js';
import { percentage } from '@filters/shared-filters';
import { RealEstateStrategy } from 'types/real-estate-strategy';

export default class ReitElement {
	public id!: string;
	public name!: string;
	public reits?: Array<{ id: string; name: string; reitDollarsInvestedInElement: string }>;
	public reitName!: string;
	public coverPhotoUrl!: string;
	public thumbnailUrl!: string;
	public status!: string;
	public projectType!: string;
	public address!: string;
	public city!: string;
	public state!: string;
	public zip!: string;
	public lat!: string;
	public lon!: string;
	public structure!: string;
	public realizedReturn!: string;
	public totalSize!: string;
	public acquisitionDate!: string;
	public exitDate!: string;
	public blendedReturnProjection!: boolean;
	public annualInterestRate!: string;
	public projectedReturnLowerBound!: string;
	public projectedReturnUpperBound!: string;
	public returnRateType!: 'PROJECTED_RETURN_RANGE' | 'ANNUAL_INTEREST_RATE' | 'NO_RETURN_PROJECTION';
	public riskScore!: string;
	public rating!: ReitElementRating;
	public strategy!: RealEstateStrategy;
	public inScopeForInvestorSubmissions!: boolean;
	public geoJson?: string;
	public submarket!: string;
	public returnRate!: string;
	public returnSortValue!: big;
	public externalReturnType!: string;
	public completed!: boolean;
	public showBlendedReturnNotice!: boolean;
	public weight!: big;

	private calculateReturnRate(): string {
		if (this.returnRateType) {
			if (this.status !== 'REDEEMED') {
				if (this.returnRateType === 'PROJECTED_RETURN_RANGE') {
					return (
						percentage(this.projectedReturnLowerBound) +
						' - ' +
						percentage(this.projectedReturnUpperBound, true)
					);
				} else if (this.returnRateType === 'ANNUAL_INTEREST_RATE') {
					return percentage(this.annualInterestRate, true);
				}
			} else {
				return percentage(this.realizedReturn, true);
			}
		}

		return '-';
	}

	private calculateReturnSortValue(): big {
		const bigLowerBound = big(this.projectedReturnLowerBound);
		const bigUpperBound = big(this.projectedReturnUpperBound);
		const bigAnnualInterest = big(this.annualInterestRate);

		if (this.completed) {
			return big(this.realizedReturn);
		}

		if (!bigLowerBound.eq(0) && !bigUpperBound.eq(0)) {
			return bigLowerBound;
		} else if (!bigAnnualInterest.eq(0)) {
			return bigAnnualInterest;
		} else {
			return big(-999);
		}
	}

	public partOfReit(reitId: string): boolean {
		return this.reits?.some((reit) => reit.id === reitId) ?? false;
	}

	public constructor(apiResponse: ReitElementApi, weight = '0') {
		Object.assign(this, apiResponse);
		this.weight = big(weight);
		this.completed = this.status === 'REDEEMED';
		this.returnSortValue = this.calculateReturnSortValue();
		this.returnRate = this.calculateReturnRate();
		this.externalReturnType = this.completed ? 'Realized Return' : 'Projected Return';
		this.showBlendedReturnNotice = !this.completed && this.blendedReturnProjection;
	}
}
